import React, { useState, useEffect, useRef, useContext, Suspense } from "react";
import { Card, Col, Row, Table, Button, Form, Spinner, Tab, Nav, Badge, OverlayTrigger, Tooltip, ButtonGroup, ToggleButton, Modal } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTable, useSortBy, useExpanded } from "react-table";
import NotyfContext from "../../contexts/NotyfContext";
import { UserSettingsContext } from "../../contexts/UserSettingsContext";
import LazyBar from "../../utils/LazyBar";
import Select from "react-select";

import authenticatedFetch from "../../utils/fetch"
import SearchOption, { SearchShortcutHint } from "../../utils/SearchOption";
import { useSearchParams } from "react-router-dom";

import DocumentList from "../../utils/DocumentList";
import ContractTypeIcon from "../../utils/ContractTypeIcon";

import { PlusCircle, MinusCircle, ArrowUp, ArrowDown, Edit2, X, Check } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faInfinity,
  faFileExcel,
  faFileContract,
} from "@fortawesome/free-solid-svg-icons";

import getActivityDateTimeDisplay from "../../utils/datetime"
import EditableExpires from "../../utils/EditableExpires";
import TaskStatusBadge from "../../utils/TaskStatusBadge";
import TaskTable, { buildTaskColumns, compareStarredOrderingDue, TOOLBAR_CONTROL_HEIGHT, TOOLBAR_BUTTON_STYLE } from "../../utils/TaskTable";


const TIER_ORDINALS = {
  CORE: 0,
  STRATEGIC: 1,
  ENTERPRISE: 2,
};

const buildProviderOption = (provider) => {
  const practices = provider.practices || [];
  const detail = practices.length > 0
    ? practices[0].name + (practices.length > 1 ? " + " + (practices.length - 1) + " more..." : "")
    : "";

  return {
    value: provider.id,
    revenueTeamId: provider.revenueTeamId,
    entityType: "provider",
    name: provider.formattedName,
    npi: provider.npi,
    detail: detail,
    // Shown as the selected value; the search also matches every practice, not just the first
    label: provider.formattedName + (provider.npi ? " [" + provider.npi + "]" : "") + (detail ? " (" + detail + ")" : ""),
    searchText: [provider.formattedName, provider.npi].concat(practices.map((practice) => practice.name)).join(" "),
  };
};

const ProviderSelect = ({ selectedProviderId, handleProviderChanged }) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [options, setOptions] = useState([]);

  const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        authenticatedFetch({
        path: "provider/h3adminprovidersearchlist",
        successHandler: function(result) {
            setIsLoaded(true);

            const newOptions = [];
            for (var i=0; i<result.length; i++)
            {
                newOptions.push(buildProviderOption(result[i]));
            }
            setOptions(newOptions)
        },
        errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
        }
        });
    }, [])

    // Cmd/Ctrl+K focuses the search
    const selectRef = useRef(null);
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && selectRef.current) {
                e.preventDefault();
                selectRef.current.focus();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    if (!isLoaded)
    {
        return (<div style={{width: "500px", marginBottom: "10px"}} className="float-end">
                  <Form.Control style={{height: "34px"}} disabled={true} type="text" placeholder="Loading..." />
                </div>  
              );
    }
    else if (error || !options)
    {
        return (<div style={{width: "500px", marginBottom: "10px"}} className="float-end">
                  <Form.Control style={{height: "34px"}} disabled={true} type="text" placeholder="Error... Please reload the page..." />
                </div>
              );
    }
    else
    {
        const customStyles = {
          control: (styles) => ({
            ...styles,
            minHeight: "34px",       // reduce overall height
            height: "34px",
          }),
          valueContainer: (styles) => ({
            ...styles,
            padding: "0 8px",
            maxHeight: "34px",
            overflowY: "auto",
          }),
          input: (styles) => ({
            ...styles,
            margin: "0px",
            padding: "0px",
          }),
          option: (styles) => ({
            ...styles,
            fontSize: "0.875rem",
            padding: "6px 12px",
          }),
          placeholder: (styles) => ({
            ...styles,
            fontSize: "0.875rem",
          }),
          singleValue: (styles) => ({
            ...styles,
            fontSize: "0.875rem",
          }),
          dropdownIndicator: (styles) => ({
            ...styles,
            padding: "0 4px",
          }),
          clearIndicator: (styles) => ({
            ...styles,
            padding: "0 4px",
          }),
          indicatorsContainer: (styles) => ({
            display: 'none'
          }),
          menu: (styles) => ({
            ...styles,
            zIndex: 10,  // above the sticky table header
          }),
        };


        return (
        <div style={{width: "500px", marginBottom: "10px"}} className="float-end navbar-search">
          <Select
            ref={selectRef}
            options={options}
            value={selectedProviderId}
            onChange={handleProviderChanged}
            placeholder="Provider quick search..."
            components={{ DropdownIndicator: () => null, Option: SearchOption }}
            styles={customStyles}
            isMulti={false}
            inputValue={inputValue}
            onInputChange={(val) => setInputValue(val)}
            menuIsOpen={inputValue.length > 0}
            filterOption={(option, rawInput) => {
              const queryWords = rawInput.trim().toLowerCase().split(/\s+/).filter(Boolean);
              // Split on whitespace/punctuation so the NPI (e.g. "[1124570932]") and
              // parenthesized org name become their own tokens too.
              const labelWords = option.data.searchText.toLowerCase().split(/[\s,()[\]]+/).filter(Boolean);
              return queryWords.every((queryWord) =>
                labelWords.some((labelWord) => labelWord.includes(queryWord))
              );
            }}
            theme={(theme) => ({
              ...theme,
              colors: {
              ...theme.colors,
                primary50: '#DEF2F1',
                primary25: '#DEF2F1',
                primary: '#3AAFA9',
              },
            })}
          />
          <SearchShortcutHint />
        </div>
        )
    }
}

const TaskAssigneeSelect = ({ handleAssigneesChanged, selectedAssignee, placeholder="Select an assignee..."}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [options, setOptions] = useState([]);


    useEffect(() => {
        authenticatedFetch({
        path: "task/availableh3assignees",
        successHandler: function(result) {
            setIsLoaded(true);

            const newOptions = [];
            for (var i=0; i<result.length; i++)
            {
                var label = result[i].firstName + ' ' + result[i].lastName;
                if (result[i].isH3Admin)
                {
                    label = label + " (H3)";
                }
    
                newOptions.push({value: result[i].userId, label:label});
                setOptions(newOptions)
            }
        },
        errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
        }
        });
    }, [selectedAssignee])

    if (!isLoaded)
    {
        return (<div style={{display: "inline-block", verticalAlign: "bottom", width: "300px"}}>
                  <Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>
                </div>  
              );
    }
    else if (error || !options)
    {
        return (<div style={{display: "inline-block", verticalAlign: "bottom", width: "300px"}}>
                  <Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>
                </div>
              );
    }
    else
    {
        const customStyles = {
          control: (styles) => ({
            ...styles,
            minHeight: TOOLBAR_CONTROL_HEIGHT + "px",       // match the toolbar buttons
            height: TOOLBAR_CONTROL_HEIGHT + "px",
          }),
          valueContainer: (styles) => ({
            ...styles,
            padding: "0 8px",
            maxHeight: TOOLBAR_CONTROL_HEIGHT + "px",
            overflowY: "auto",
          }),
          input: (styles) => ({
            ...styles,
            margin: "0px",
            padding: "0px",
          }),
          dropdownIndicator: (styles) => ({
            ...styles,
            padding: "0 4px",
          }),
          clearIndicator: (styles) => ({
            ...styles,
            padding: "0 4px",
          }),
          indicatorsContainer: (styles) => ({
            ...styles,
            height: TOOLBAR_CONTROL_HEIGHT + "px",
          }),
        };

        return (
        <div style={{display: "inline-block", verticalAlign: "bottom", width: "300px"}}>
          <Select
              options={options}
              styles={customStyles}
              onChange={(e) => {handleAssigneesChanged(e)}}
              value={selectedAssignee}
              placeholder={placeholder}
              isMulti={false}
              isClearable={true}
              theme={(theme) => ({
                  ...theme,
                  colors: {
                  ...theme.colors,
                    primary50: '#DEF2F1',
                    primary25: '#DEF2F1',
                    primary: '#3AAFA9',
                  },
                })}
          />
        </div>
        )
    }
}

const RevenueTeamNotes = ({ id, notes, enrollmentNotificationEmails, taskResolutionReportEmails, supportEmailAlias, updated, reload }) => {
  if (notes == null)
  {
    notes = '';
  }

  const [saving, setSaving] = useState(false);
  const [originalNotes, setOriginalNotes] = useState(notes);
  const [localNotes, setLocalNotes] = useState(notes);
  const notyf = useContext(NotyfContext);

  const { authorities } = useContext(UserSettingsContext);

  const handleCancelClicked = (e) => {
    e.preventDefault();

    if(window.confirm("Discard changes?"))
    {
      setLocalNotes(originalNotes);
    }
  }


  var enrollmentNotificationEmailsDisplay = "";
  if (enrollmentNotificationEmails != null)
  {
    enrollmentNotificationEmailsDisplay = enrollmentNotificationEmails.join(", ");
  }

  var taskResolutionReportEmailsDisplay = "";
  if (taskResolutionReportEmails != null)
  {
    taskResolutionReportEmailsDisplay = taskResolutionReportEmails.join(", ");
  }

  const handleSaveClicked = (e) => {
    e.preventDefault();
    setSaving(true);
    authenticatedFetch({
      method: "PUT",
      requestData: {notes: localNotes},
      path: "revenueteam/" + id,
      successHandler: function(result) {
        setSaving(false);
        setOriginalNotes(result.notes);
        reload();
        notyf.open({type: "success", message: "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function(error) {
        setSaving(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const onNotesChanged = (e) => {
    e.preventDefault();
    setLocalNotes(e.target.value);
  }

  const canViewDocuments = authorities.includes("H3_ADMIN_REVENUE_TEAM_DOCUMENT_VIEW") || authorities.includes("H3_ADMIN_REVENUE_TEAM_DOCUMENT_EDIT");
  const canEditDocuments = authorities.includes("H3_ADMIN_REVENUE_TEAM_DOCUMENT_EDIT");

  return (
  <>
      <td>&nbsp;</td>
      <td colSpan={canViewDocuments ? 5 : 14}>
        <Card>
            <Card.Body>
              <h5>Notes</h5>
              <br />
              <Form.Control onChange={(e) => {onNotesChanged(e)}} value={localNotes} as="textarea" style={{height: "150px"}}></Form.Control>
              <br />
              {saving ? <Spinner animation="border" size="sm" /> :
              originalNotes !== localNotes ?
              <>
                <Button variant="primary" size="sm" onClick={(e) => handleSaveClicked(e)}>Save</Button>&nbsp;
                <Button variant="secondary" size="sm" onClick={(e) => handleCancelClicked(e)}>Cancel</Button>
                <br /><br />
              </>
              : <></>
              }
              <h5>Enrollment Notification Emails</h5>
              {enrollmentNotificationEmailsDisplay}
              <br /><br />
              <h5>Task Resolution Report Emails</h5>
              {taskResolutionReportEmailsDisplay}
              <br /><br />
              <h5>Support Email Alias</h5>
              {supportEmailAlias ? supportEmailAlias : ""}
              <br /><br />
              <span style={{fontSize: "0.8em", fontStyle:"italic"}}>Updated: {updated}</span>
            </Card.Body>
          </Card>
      </td>
      {canViewDocuments ?
      <>
        <td>&nbsp;</td>
        <td colSpan={8}>
          <Card style={{minHeight: "325px"}}>
            <Card.Header style={{marginBottom: '-25px'}}>
              <Card.Title style={{marginBottom: '-15px'}}>Documents</Card.Title>
              <br />
            </Card.Header>
            <Card.Body>
              <DocumentList entityId={id} entityType="revenueteam" canEdit={canEditDocuments} isH3AdminFile={true}/>
            </Card.Body>
          </Card>
        </td>
      </> : <></>
      }
      <td>&nbsp;</td>
  </>
  );
};

const TIER_BADGE_BACKGROUNDS = {
  CORE: "#3B4F56",
  STRATEGIC: "#3AAFA9",
  ENTERPRISE: "#2B7A78",
};

const TIER_LABELS = {
  CORE: "Core",
  STRATEGIC: "Strategic",
  ENTERPRISE: "Enterprise",
};

const TierBadge = ({ tier }) => {
  if (!tier)
  {
    tier = "CORE";
  }

  const background = TIER_BADGE_BACKGROUNDS[tier];

  if (!background)
  {
    return null;
  }

  return (
    <Badge pill bg="" style={{backgroundColor: background, color: "#FEFFFF"}}>
      {TIER_LABELS[tier]}
    </Badge>
  );
};

const ContractRatio = ({ uniqueCount, contractCount }) => {

  var className = "";
  var displayText;

  if (contractCount === null)
  {
    displayText = <>{uniqueCount}/<FontAwesomeIcon icon={faInfinity} /></>;
  }
  else
  {
    displayText = uniqueCount + "/" + contractCount;
  
    var ratio = 0.0;
    if (contractCount === 0 && uniqueCount > 0)
    {
      // Nothing allotted on the contract, so red flag any usage at all
      ratio = 1.1;
    }
    if (contractCount > 0)
    {
      ratio = uniqueCount / contractCount;
    }
    
    if (ratio > 1.0)
    {
      className = "text-danger";
    }
    else if (ratio >= 0.9)
    {
      className = "text-warning";
    }
  }

  return <span className={className}>{displayText}</span>;
}


const RevenueTeam = ({ id, name, state, defaultTaskAssignee, contractType, contractCarriers, contractProviders, contractFacilities, contractUsers, uniqueNpiCount, uniqueCarrierCount, uniqueFacilityCount, uniqueUserCount, startDate, h3LeadershipOwner, tier, lastVisitDate, dueForVisit, reload }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);
  const notyf = useContext(NotyfContext);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localLastVisitDate, setLocalLastVisitDate] = useState(lastVisitDate);

  const handleRevenueTeamClicked = (e) => {
    e.preventDefault();
    
    authenticatedFetch({
      path: "auth/revenueteam",
      method: "PUT",
      requestData: {revenueTeamId: id},
      successHandler: function() {
        sessionStorage.setItem("revenueTeamId", id);
        if (searchParams.get("destination") && searchParams.get("destination") !== '')
        {
          // Note that this will bounce them back to the dashboard if the destination was from a different rev team
          navigate("/?destination=" + encodeURIComponent(searchParams.get("destination")));
        }
        else
        {
          navigate("/");
        }
      },
      errorHandler: function() {
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const handleExportClicked = (e) => {
    e.preventDefault();

    setIsExporting(true);

    authenticatedFetch({
      method: "GET",
      path:  "report/revenueteamuniques/" + id,
      successHandler: function(result) {
        window.open(result.url);
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }
  
  const handleEditLastVisitDate = (newDate) => {
    setLocalLastVisitDate(newDate);
  };

  const handleEditClicked = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setLocalLastVisitDate(lastVisitDate);
    setEditing(false);
  };

  const handleSaveEdit = () => {
    setSaving(true);
    authenticatedFetch({
      method: "PUT",
      requestData: {lastVisitDate: localLastVisitDate},
      path: "revenueteam/" + id + "/metadata",
      successHandler: function() {
        setSaving(false);
        setEditing(false);
        reload();
        notyf.open({type: "success", message: "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setSaving(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  };

  var startDateDisplay = "";
  if (startDate != null)
  {
    var parts = startDate.split("-");
    var dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
    startDateDisplay = dateObj.toLocaleDateString("en-US");
  }

  return (
  <>
      <td>{id}</td>
      <td><ContractTypeIcon contractType={contractType} defaultToService={true} /></td>
      <td><TierBadge tier={tier} /></td>
      <td style={{minWidth: "200px"}}><a href="#" onClick={(e) => {handleRevenueTeamClicked(e)}}>{name}</a></td>
      <td>{state}</td>
      <td>{defaultTaskAssignee}</td>
      <td>{h3LeadershipOwner}</td>
      <td style={dueForVisit ? {backgroundColor: "rgba(220,53,69,0.3)", "--bs-table-accent-bg": "transparent"} : {}}><EditableExpires warnWithColors={false} localExpires={localLastVisitDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleEditLastVisitDate} /></td>
      <td>{startDateDisplay}</td>
      <td style={{borderLeft: "1px solid #E8EAED"}}><ContractRatio uniqueCount={uniqueNpiCount} contractCount={contractProviders} /></td>
      <td><ContractRatio uniqueCount={uniqueCarrierCount} contractCount={contractCarriers} /></td>
      <td><ContractRatio uniqueCount={uniqueFacilityCount} contractCount={contractFacilities} /></td>
      <td><ContractRatio uniqueCount={uniqueUserCount} contractCount={contractUsers} /></td>
      <td>
        {isExporting ? 
        <Spinner animation="border" size="sm" variant="primary" className="float-end"/>
        :
        <OverlayTrigger
          trigger={["hover", "focus"]}
          placement="bottom"
          overlay={<Tooltip>Unique Entities</Tooltip>}
        >
          <span>
            <FontAwesomeIcon onClick={(e) => {handleExportClicked(e)}} icon={faFileExcel} size="lg" style={{color: '#2B7A78', cursor: "pointer", marginRight: "2px", marginLeft: "1px"}}/> 
          </span>
        </OverlayTrigger>
        }
      </td>
      <td style={{whiteSpace: "nowrap"}}>
        {saving ? (
          <Spinner animation="border" size="sm" variant="primary" />
        ) : editing ? (
          <span style={{whiteSpace: "nowrap"}}>
            <X cursor="pointer" style={{height: '16px', width: '16px', marginRight: '8px'}} onClick={() => {handleCancelEdit()}}/>
            <Check cursor="pointer" style={{height: '16px', width: '16px'}} onClick={() => {handleSaveEdit()}} className="text-success"/>
          </span>
        ) : (
          <Edit2 cursor="pointer" style={{height: '14px', width: '14px'}} onClick={() => {handleEditClicked()}}/>
        )}
      </td>
  </>
  );
};

const SortableRevenueTeamTable = ({ isLoaded, error, revenueTeamList, reload }) => {
  const data = revenueTeamList; 

  const columns = React.useMemo(
    () => [
      {
        // Build our expander column
        id: "expander", // Make sure it has an ID
        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
          <span {...getToggleAllRowsExpandedProps()}>
            {isAllRowsExpanded ? (
              <MinusCircle className="feather" />
            ) : (
              <PlusCircle className="feather" />
            )}
          </span>
        ),
        Cell: ({ row }) =>
          // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
          // to build the toggle for expanding a row
          row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({

              })}
            >
              {row.isExpanded ? (
                <MinusCircle className="feather" />
              ) : (
                <PlusCircle className="feather" />
              )}
            </span>
          ) : null,
      },
      {
        Header: "ID",
        accessor: "id",
        key: "id",
      },
      {
        Header: () => <FontAwesomeIcon icon={faFileContract} />,
        accessor: "contractType",
        key: "contractType",
      },
      {
        Header: "Tier",
        accessor: "tier",
        key: "tier",
        sortType: (rowA, rowB) => (TIER_ORDINALS[rowA.values.tier || "CORE"] ?? -1) - (TIER_ORDINALS[rowB.values.tier || "CORE"] ?? -1),
      },
      {
        Header: "Name",
        accessor: "name",
        key: "name",
      },
      {
        Header: "State",
        accessor: "state",
        key: "state",
      },
      {
        Header: "Task Assignee",
        accessor: "defaultTaskAssignee",
        key: "defaultTaskAssignee",
      },
      {
        Header: "H3 Leadership",
        accessor: "h3LeadershipOwner",
        key: "h3LeadershipOwner",
      },
      {
        Header: "Last Visit",
        accessor: "lastVisitDate",
        key: "lastVisitDate",
      },
      {
        accessor: "dueForVisit",
        key: "dueForVisit",
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        key: "startDate",
      },
      {
        Header: "Providers",
        accessor: "contractProviders",
        key: "contractProviders",
        sortType: (rowA, rowB) => rowA.values.uniqueNpiCount - rowB.values.uniqueNpiCount,
      },
      {
        Header: "Payors / TIN",
        accessor: "contractCarriers",
        key: "contractCarriers",
        sortType: (rowA, rowB) => rowA.values.uniqueCarrierCount - rowB.values.uniqueCarrierCount,
      },
      {
        Header: "Locations",
        accessor: "contractFacilities",
        key: "contractFacilities",
        sortType: (rowA, rowB) => rowA.values.uniqueFacilityCount - rowB.values.uniqueFacilityCount,
      },
      {
        Header: "Users",
        accessor: "contractUsers",
        key: "contractUsers",
      },
      {
        accessor: "export",
        key: "export",
      },
      {
        id: "editWidget",
        Header: "",
      },
      {
        accessor: "uniqueNpiCount",
        key: "uniqueNpiCount",
      },
      {
        accessor: "uniqueCarrierCount",
        key: "uniqueCarrierCount",
      },
      {
        accessor: "uniqueFacilityCount",
        key: "uniqueFacilityCount",
      },
      {
        accessor: "uniqueUserCount",
        key: "uniqueUserCount",
      }
    ],
  []
  )
  
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        autoResetSortBy: false,
        autoResetExpanded: false,
        initialState: {
          hiddenColumns: ['uniqueNpiCount', 'uniqueCarrierCount', 'uniqueFacilityCount', 'uniqueUserCount', 'dueForVisit'],
          sortBy: [
              {
                  id: 'name',
                  desc: false
              }
          ]
        }
      },
      useSortBy,
      useExpanded
    );

  return (
    <Card>
      <Card.Body>
      <Table striped hover {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            return (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                    const { key, style, ...restHeaderProps } = (column.id !== "expander" && column.id !== "updated" && column.id !== "contractUsers" && column.id !== "export" && column.id !== "editWidget") ? column.getHeaderProps(column.getSortByToggleProps()) : column.getHeaderProps();
                    return (
                    <th {...restHeaderProps} key={key} style={{...style, whiteSpace: "nowrap"}}>
                      {column.render("Header")}
                    {(column.id !== "expander" && column.id !== "updated" && column.id !== "contractUsers" && column.id !== "export" && column.id !== "editWidget") ? (
                      <>
                        {column.isSorted ? (
                              column.isSortedDesc ? (
                                <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                              ) : (
                                <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                              )
                            ) : (
                              <FontAwesomeIcon icon={faSort} className="ms-2" />
                          )}
                      </>
                      ) : 
                      <></> 
                    }
                    </th>
                  );
                }
                )}
              </tr>
            );
            }
          )}
        </thead>
        <tbody {...getTableBodyProps()}>
          {
          error ? (<tr><td colSpan={16}>Something went wrong; please reload the page...</td></tr>) : (
            !isLoaded ? (<tr><td colSpan={16}>Loading...</td></tr>) : (
            rows.map((row, i) => {
              prepareRow(row);
              const { key, ...restRowProps } = row.getRowProps();
              if (row.id.indexOf(".") > 0)
              {
                return (
                <tr {...restRowProps} key={key}>
                  <RevenueTeamNotes key={row.values.id}  id={row.values.id} notes={row.values.name} enrollmentNotificationEmails={row.values.defaultTaskAssignee} taskResolutionReportEmails={row.values.contractProviders} supportEmailAlias={row.values.contractType} updated={row.values.state} reload={reload} />
                </tr>
                );
              }

              return (
                <tr {...restRowProps} key={key}>
                  <td>{row.cells[0].render("Cell")}</td>
                  <RevenueTeam key={row.values.id} id={row.values.id} name={row.values.name} state={row.values.state} defaultTaskAssignee={row.values.defaultTaskAssignee} contractType={row.values.contractType} contractProviders={row.values.contractProviders} contractCarriers={row.values.contractCarriers} contractFacilities={row.values.contractFacilities} contractUsers={row.values.contractUsers} uniqueNpiCount={row.values.uniqueNpiCount} uniqueCarrierCount={row.values.uniqueCarrierCount} uniqueFacilityCount={row.values.uniqueFacilityCount} uniqueUserCount={row.values.uniqueUserCount} startDate={row.values.startDate} updated={row.values.updated} h3LeadershipOwner={row.values.h3LeadershipOwner} tier={row.values.tier} lastVisitDate={row.values.lastVisitDate} dueForVisit={row.values.dueForVisit} reload={reload} />
                </tr>
              );
            })))
          }
          {(!error && isLoaded) &&
          <tr>
            <td colSpan={16}>Total: <b>{rows.filter((row) => row.depth === 0).length}</b></td>
          </tr>
          }
        </tbody>
      </Table>
      </Card.Body>
    </Card>
    );
}

const TeamTaskViewToggle = ({ selectedTeamTaskView, handleTeamTaskViewChanged, teamTasksLoaded, dueNowCount=null, assigneeCount=null}) => {
  return (
      <ButtonGroup className="me-auto" size="sm">
          <ToggleButton
              disabled={!teamTasksLoaded}
              key="DUE_NOW"
              id="DUE_NOW"
              type="radio"
              variant="outline-primary"
              style={{...TOOLBAR_BUTTON_STYLE, color: selectedTeamTaskView === "DUE_NOW" ? "white" : ""}}
              value="DUE_NOW"
              checked={selectedTeamTaskView === "DUE_NOW"}
              onChange={(e) => {handleTeamTaskViewChanged(e.target.value)}}
          >Due Now{dueNowCount !== null ? " (" + dueNowCount + ")" : ""}</ToggleButton>
          <ToggleButton
              disabled={!teamTasksLoaded}
              key="ASSIGNEE"
              id="ASSIGNEE"
              type="radio"
              variant="outline-primary"
              style={{...TOOLBAR_BUTTON_STYLE, color: selectedTeamTaskView === "ASSIGNEE" ? "white" : ""}}
              value="ASSIGNEE"
              checked={selectedTeamTaskView === "ASSIGNEE"}
              onChange={(e) => {handleTeamTaskViewChanged(e.target.value)}}
          >By Assignee{assigneeCount !== null ? " (" + assigneeCount + ")" : ""}</ToggleButton>
      </ButtonGroup>
  )
}

// Matches the server's compareNullable: blanks first.
const compareNullable = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return a < b ? -1 : a > b ? 1 : 0;
};

// Same order as the server's TaskResponse.compareTo, minus the starred step.
const compareIgnoringStars = (rowA, rowB) =>
  compareNullable(rowA.task.due, rowB.task.due) ||
  compareNullable(rowA.task.providerLastNameFirstName, rowB.task.providerLastNameFirstName) ||
  compareNullable(rowA.task.updated, rowB.task.updated);

// Local YYYY-MM-DD, comparable with the date-only strings the server sends for task.due
const getTodayString = () => {
  const now = new Date();
  return now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
};

// Blank next-action dates are kept: an unscheduled task shouldn't silently disappear from the default view.
const isDueNow = (task, today) => !task.due || task.due <= today;

const DueFilterToggle = ({ idPrefix, showAll, onChange, dueNowCount, totalCount }) => {
  return (
    <ButtonGroup size="sm">
      <ToggleButton id={idPrefix + "_DUE_NOW"} type="radio" variant="outline-primary" style={{...TOOLBAR_BUTTON_STYLE, color: !showAll ? "white" : ""}} value="DUE_NOW" checked={!showAll} onChange={() => onChange(false)}>
        Due Now ({dueNowCount})
      </ToggleButton>
      <ToggleButton id={idPrefix + "_ALL"} type="radio" variant="outline-primary" style={{...TOOLBAR_BUTTON_STYLE, color: showAll ? "white" : ""}} value="ALL" checked={showAll} onChange={() => onChange(true)}>
        All ({totalCount})
      </ToggleButton>
    </ButtonGroup>
  );
};

// Starred tasks in their displayed order -> the {id, ordering} updates needed to move movedId above targetId.
// Orderings are renumbered 0..n-1 across the whole starred list (not just the visible rows), and only tasks whose
// ordering actually changes are returned.
const getReorderUpdates = (starredTasksInOrder, movedId, targetId) => {
  const moved = starredTasksInOrder.find((task) => task.id === movedId);
  const withoutMoved = starredTasksInOrder.filter((task) => task.id !== movedId);
  const targetIndex = withoutMoved.findIndex((task) => task.id === targetId);
  if (!moved || targetIndex < 0) {
    return [];
  }

  withoutMoved.splice(targetIndex, 0, moved);
  return withoutMoved
    .map((task, index) => ({ task, ordering: index }))
    .filter(({ task, ordering }) => task.ordering !== ordering)
    .map(({ task, ordering }) => ({ id: task.id, ordering: ordering }));
};

const TaskList = ({taskTab, showAllDue, setShowAllDue, error, isLoaded, data, reloadTaskLists, selectedTeamTaskView, handleTeamTaskViewChanged, handleAssigneesChanged, selectedAssignee, teamTasksLoaded}) => {
  const notyf = useContext(NotyfContext);

  // My Tasks: orderings applied locally while a reorder is being saved; cleared when fresh data arrives
  const [orderOverrides, setOrderOverrides] = useState({});
  // id of the task whose reorder is being saved; further reordering is blocked until the save and refresh finish
  const [savingOrderId, setSavingOrderId] = useState(null);
  useEffect(() => {
    setOrderOverrides((current) => Object.keys(current).length > 0 ? {} : current);
    setSavingOrderId(null);
  }, [data]);

  var taskData = []
  var dueNowCount = 0;
  var starredInOrder = [];
  if (isLoaded && !error)
  {
    // Match the data structure of the legacy task page so we can use its components
    // The server orders tasks starred-first; Team Tasks hides stars, so re-order ties without them.
    let rows = data.map((task) => {
      return {task: task}
    });
    if (taskTab === "mytasks")
    {
      rows = rows.map((row) => orderOverrides[row.task.id] === undefined ? row : {task: {...row.task, ordering: orderOverrides[row.task.id]}});
      rows.sort(compareStarredOrderingDue);
      starredInOrder = rows.filter((row) => row.task.starred).map((row) => row.task);
    }
    taskData = taskTab === "teamtasks" ? rows.sort(compareIgnoringStars) : rows;

    if (taskTab === "mytasks")
    {
      const today = getTodayString();
      dueNowCount = rows.filter((row) => isDueNow(row.task, today)).length;
      if (!showAllDue)
      {
        taskData = taskData.filter((row) => isDueNow(row.task, today));
      }
    }
  }

  const columns = React.useMemo(
    () => buildTaskColumns({ reloadTaskLists, switchTeam: true, canStar: taskTab === "mytasks", showStar: taskTab === "mytasks", reorderable: taskTab === "mytasks" }),
  []
  )

  // My Tasks' default order: starred first (in the user's ordering), then Next Action
  const initialState = {
    sortBy: (taskTab === "mytasks") ? [
      { id: "task.starred", desc: false },
      { id: "task.due", desc: false }
    ] : [
      { id: "task.due", desc: false }
    ]
  };

  const handleReorder = (movedId, targetId) => {
    const updates = getReorderUpdates(starredInOrder, movedId, targetId);
    if (updates.length === 0) {
      return;
    }

    setOrderOverrides(Object.fromEntries(updates.map((update) => [update.id, update.ordering])));
    setSavingOrderId(movedId);

    authenticatedFetch({
      path: "task/order",
      method: "PUT",
      requestData: {taskOrderUpdates: updates},
      successHandler: function() {
        reloadTaskLists();
      },
      errorHandler: function() {
        setOrderOverrides({});
        setSavingOrderId(null);
        notyf.open({type: "error", message: "Something went wrong saving the new order; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  };

  return (
    <TaskTable columns={columns} data={taskData} initialState={initialState} isLoaded={isLoaded} error={error} reloadTaskLists={reloadTaskLists} reorder={taskTab === "mytasks" ? { onReorder: handleReorder, savingId: savingOrderId } : undefined}>
      {taskTab === "mytasks" &&
        <DueFilterToggle idPrefix="MY_TASKS" showAll={showAllDue} onChange={setShowAllDue} dueNowCount={dueNowCount} totalCount={isLoaded && !error ? data.length : 0}/>
      }
      {taskTab === "teamtasks" &&
        <div className="d-flex align-items-center" style={{gap: "8px"}}>
          <TeamTaskViewToggle selectedTeamTaskView={selectedTeamTaskView} handleTeamTaskViewChanged={handleTeamTaskViewChanged} teamTasksLoaded={teamTasksLoaded} dueNowCount={selectedTeamTaskView === "DUE_NOW" && isLoaded && !error ? data.length : null} assigneeCount={selectedTeamTaskView === "ASSIGNEE" && isLoaded && !error ? data.length : null}/>
          {selectedTeamTaskView === "ASSIGNEE" &&
            <>
            &nbsp;&nbsp;
            <TaskAssigneeSelect handleAssigneesChanged={handleAssigneesChanged} selectedAssignee={selectedAssignee} size="sm"/>
            </>
          }
        </div>
      }
    </TaskTable>
  )
}

const TeamWorkload = ({refresh}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [clickedBucketLabel, setClickedBucketLabel] = useState("");
  const [clickedDetails, setClickedDetails] = useState([]);
  const [clickedFirstName, setClickedFirstName] = useState("");

  useEffect(() => {
    setIsLoaded(false);
    setClickedBucketLabel("");
    setClickedDetails([]);
    setClickedFirstName("");
    authenticatedFetch({
      path: "task/workload/",
      successHandler: function(result) {
          setIsLoaded(true);
          setData(result);
      },
      errorHandler: function(error) {
          setIsLoaded(true);
          setError(error);
      }
    });
  }, [refresh])

  if (!isLoaded)
  {
    return (
      <Card>
        <Card.Body>
        Loading...
        </Card.Body>
      </Card>
    )
  }
  else if (error || !data)
  {
      return (
        <Card>
          <Card.Body>
          Error... Please reload the page...
          </Card.Body>
        </Card>
      )
  }
  else
  {
    const charts = data.map((userCount, i) => {
      const labels = Object.keys(userCount.counts);
      
      // Build background colors list by looking at every value in the counts dict and comparing to warnThreshold
      const backgroundColors = Object.values(userCount["counts"]).map((count) => {
          let backgroundColor = `rgb(58, 175, 169, .7)`;
          if (count > userCount["dangerThreshold"]) {
            backgroundColor = `rgb(217, 83, 79, .7)`;
          }
          else if (count > userCount["warnThreshold"]) {
            backgroundColor = `rgb(229, 165, 75, .7)`;
          }

          return backgroundColor;
        }
      );

      const chartData = {
        labels,
        datasets: [
          {
            label: userCount["firstName"],
            backgroundColor: backgroundColors,
            data: Object.values(userCount["counts"]),
            details: userCount["details"]
          }
        ]
      }
    
      const options = {
        maintainAspectRatio: false,
        indexAxis: 'y',
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              stepSize: 1
            }
          }],
          xAxes: [
            {
              stacked: false,
              gridLines: {
                color: "transparent",
              },
            },
          ],
        },
        onClick: (evt, activeEls) => {
          if (!activeEls || !activeEls.length) return;

          const barIndex = activeEls[0].index ?? activeEls[0]._index;
          const bucketLabel = labels[barIndex];

          setClickedBucketLabel(bucketLabel);
          setClickedDetails(userCount.details?.[bucketLabel] ?? []);
          setClickedFirstName(userCount.firstName);
          setShow(true);
        }
      };
      return (
        <div style={{height: "180px", marginBottom: "60px", marginTop: "10px"}} key={i}>
          <h4 style={{textAlign: "center"}}>{userCount["firstName"]}</h4>
          <Suspense fallback={<div>Loading...</div>}>
            <LazyBar height={160} data={chartData} options={options} />
          </Suspense>
          <TaskWorkloadModal show={show} onClose={() => setShow(false)} firstName={clickedFirstName} bucketLabel={clickedBucketLabel} details={clickedDetails ? clickedDetails : []} />
        </div>
      )
    }, this);
  
    return (
      <Card>
        <Card.Body>
          {charts}
        </Card.Body>
      </Card>
    )
  }
}

const TaskDueDateChangeModal = ({show, onClose, bucketLabel, details}) => { 
  function handleTaskClicked(e, taskId)
  {
    e.preventDefault();
    window.location = "/tasks?task=" + taskId;
  }

  const detailRows = details && details.map ((detail, i) => {
    var parts = detail.prevDue.split("-");
    var prevDue = new Date(parts[0], parts[1] - 1, parts[2]);
    var partsNew = detail.newDue.split("-");
    var newDue = new Date(partsNew[0], partsNew[1] - 1, partsNew[2]);

    return (
      <tr key={i}>
        <td><a href="#" onClick={(e) => {handleTaskClicked(e, detail.taskId)}}>{detail.revenueTeamName}</a></td>
        <td>{prevDue.toLocaleDateString("en-US")}</td>
        <td>{newDue.toLocaleDateString("en-US")}</td>
      </tr>
    )
  }, this);

  var bucketLabelParts = bucketLabel.split("-");
  var bucketLabelDisplay = new Date(bucketLabelParts[0], bucketLabelParts[1] - 1, bucketLabelParts[2]);

  return (
    <Modal show={show} onHide={onClose} scrollable={true} dialogClassName={"modal-850"}>
    <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
      <Modal.Title>
        Next Action Date Change Details | {bucketLabelDisplay.toLocaleDateString("en-US")}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Table striped bordered hover size="sm"> 
        <thead>
          <tr>
            <td>Task for Revenue Team</td>
            <td>Prev. Next Action Date</td>
            <td>New Next Action Date</td>
          </tr>
        </thead>
        <tbody>
          {detailRows} 
        </tbody>
      </Table>
    </Modal.Body>
    <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
      <Button onClick={onClose}>Close</Button>
    </Modal.Footer>
  </Modal>
  )
}

const TaskWorkloadModal = ({show, onClose, firstName, bucketLabel, details}) => { 
  function handleTaskClicked(e, taskId)
  {
    e.preventDefault();
    window.location = "/tasks?task=" + taskId;
  }

  const detailRows = details && details.map ((detail, i) => {
    return (
      <tr key={i} onClick={(e) => {handleTaskClicked(e, detail.taskId)}} style={{cursor: "pointer"}}>
        <td>{detail.revenueTeamName}</td>
        <td>{detail.taskCategory}</td>
        <td>{detail.taskSummary}</td>
        <td><TaskStatusBadge status={detail.taskStatus} /></td>
      </tr>
    )
  }, this);

  var bucketLabelParts = bucketLabel.split("-");
  var bucketLabelDisplay = new Date(bucketLabelParts[0], bucketLabelParts[1] - 1, bucketLabelParts[2]);

  return (
    <Modal show={show} onHide={onClose} scrollable={true} dialogClassName={"modal-850"}>
    <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
      <Modal.Title>
        Task Workload Details | {firstName} | {bucketLabelDisplay.toLocaleDateString("en-US")}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Table striped bordered hover size="sm"> 
        <thead>
          <tr>
            <td>Revenue Team</td>
            <td>Category</td>
            <td>Summary</td>
            <td>Status</td>
          </tr>
        </thead>
        <tbody>
          {detailRows} 
        </tbody>
      </Table>
    </Modal.Body>
    <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
      <Button onClick={onClose}>Close</Button>
    </Modal.Footer>
  </Modal>
  )
}

const EmployeeKpisTaskDueChangesNoNoteChart = ({userId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState({});
  const [show, setShow] = useState(false);
  const [clickedBucketLabel, setClickedBucketLabel] = useState("");
  const [clickedDetails, setClickedDetails] = useState([]);

  const chartRef = useRef(null);

  useEffect(() => {
    if (userId && userId !== 0)
    {
      setIsLoaded(false);
      setClickedBucketLabel("");
      setClickedDetails([]);
      setError(null);
      authenticatedFetch({
        path: "task/employeekpis/taskduechangesnonote/" + userId,
        successHandler: function(result) {
          setIsLoaded(true);
          setData(result);
        },
        errorHandler: function(error) {
          setIsLoaded(true);
          setError(error);
        }
      });
    }
  }, [userId])

  if (!userId || userId === 0)
  {
    return (
      <div style={{height: "250px", marginBottom: "40px", marginTop: "10px"}}>
        &nbsp;
      </div>
    )
  }
  else if (!isLoaded)
  {
    return (
      <div style={{height: "250px", marginBottom: "40px", marginTop: "10px"}}>
        Loading...
      </div>
    )
  }
  else if (error || !data || !data.counts)
  {
      return (
        <div style={{height: "250px", marginBottom: "40px", marginTop: "10px"}}>
          Error... Please reload the page...
        </div>
      )
  }
  else
  {
    const backgroundColors = Object.values(data.counts).map((count) => {
        let backgroundColor = `rgb(58, 175, 169, .7)`;
        // if (count > userCount["dangerThreshold"]) {
        //   backgroundColor = `rgb(217, 83, 79, .7)`;
        // }
        // else if (count > userCount["warnThreshold"]) {
        //   backgroundColor = `rgb(229, 165, 75, .7)`;
        // }

        return backgroundColor;
      }
    );

    const chartData = {
      labels: Object.keys(data.counts),
      datasets: [
        {
          label: data.firstName,
          backgroundColor: backgroundColors,
          data: Object.values(data.counts),
          details: data.details
        }
      ]
    }
  
    const options = {
      maintainAspectRatio: false,
      indexAxis: 'y',
      legend: {
        display: false,
      },
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true,
            stepSize: 1
          }
        }],
        xAxes: [
          {
            stacked: false,
            gridLines: {
              color: "transparent",
            },
          },
        ],
      },
      onClick: (evt, activeEls) => {
        if (!activeEls || !activeEls.length) return;

        const barIndex = activeEls[0]._index;        

        const chart = chartRef.current.chartInstance;
        const bucketLabel = chart.data.labels[barIndex];
        const ds = chart.data.datasets[0];
        
        setClickedBucketLabel(bucketLabel);
        setClickedDetails(ds.details[bucketLabel]);
        setShow(true);
      }
    }

    return (
      <div style={{height: "250px", marginBottom: "40px", marginTop: "10px"}}>
        <Suspense fallback={<div>Loading...</div>}>
          <LazyBar height={160} data={chartData} options={options} ref={chartRef} />
        </Suspense>
        <TaskDueDateChangeModal show={show} onClose={() => setShow(false)} bucketLabel={clickedBucketLabel} details={clickedDetails ? clickedDetails : []} />
      </div>
    )    
  }
}

const EmployeeKpisDaysToFirstTouch = ({userId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState({});

  useEffect(() => {
    if (userId && userId !== 0)
    {
      setIsLoaded(false);
      setError(null);
      setData({});
      authenticatedFetch({
        path: "task/employeekpis/taskfirsttouchdays/" + userId,
        successHandler: function(result) {
          setIsLoaded(true);
          setData(result);
        },
        errorHandler: function(error) {
          setIsLoaded(true);
          setError(error);
        }
      });
    }
  }, [userId])

  if (!userId || userId === 0)
  {
    return (
        <div style={{ height: "200px", paddingTop: "40px", width: "100%"}}>
          <div style={{fontSize: "2.5em", textAlign: "center", width: "100%", marginBottom: "50px"}}>&nbsp;</div>
        </div>
    )
  }
  else if (!isLoaded)
  {
    return (
        <div style={{ height: "200px", paddingTop: "70px", width: "100%"}}>
          <div style={{fontSize: "1.5em", textAlign: "center", width: "100%", marginBottom: "50px"}}>Loading...</div>
        </div>
    )
  }
  else if (error || !data || data.days === undefined || data.percentChange === undefined)
  {
      return (
        <div style={{ height: "200px", paddingTop: "70px", width: "100%"}}>
          <div style={{fontSize: "1.5em", textAlign: "center", width: "100%", marginBottom: "50px"}}>Error... Please reload the page...</div>
        </div>
      )
  }
  else
  {
    // Round to 1 decimal
    const roundedDays = data.days === null ? "(n/a)" : data.days.toFixed(1) + " days";

    const percentChangeBadge = data.percentChange === null ? <Badge bg="" style={{fontSize: "1.3em"}} className="badge-soft-secondary me-2">(n/a)</Badge>:
      (data.percentChange > 0 ?
        <Badge bg="" style={{fontSize: "1.3em"}} className="badge-soft-danger me-2"><ArrowUp size={22} /> {data.percentChange.toFixed(1)}%</Badge>
        :
        (data.percentChange < 0 ?
          <Badge bg="" style={{fontSize: "1.3em"}} className="badge-soft-success me-2"><ArrowDown size={22} /> {data.percentChange.toFixed(1)}%</Badge>
          :
          <Badge bg="" style={{fontSize: "1.3em"}} className="badge-soft-secondary me-2">0.0%</Badge>
        )
      );

    return (
      <div style={{ height: "200px", paddingTop: "40px", width: "100%"}}>
        <div style={{fontSize: "3.5em", textAlign: "center", width: "100%", marginBottom: "50px"}}>{roundedDays}</div>
        {percentChangeBadge} vs. previous 90 days
      </div>
    );
  }
};


const EmployeeKpis = () => {
  const [selectedAssignee, setSelectedAssignee] = useState(null);

  const handleAssigneesChanged = (assignee) => {
    setSelectedAssignee(assignee);
  }

  return (
    <React.Fragment>
      <Row>
        <Col lg={12} style={{paddingTop: "10px", paddingBottom: "10px", marginLeft: "3px"}}>
          <TaskAssigneeSelect handleAssigneesChanged={handleAssigneesChanged} selectedAssignee={selectedAssignee} placeholder={"Select a team member..."} size="sm"/>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Card.Title style={{marginBottom: "-20px"}}>Task Next Action Date Changes with no Note</Card.Title>
            </Card.Header>
            <Card.Body>
             <EmployeeKpisTaskDueChangesNoNoteChart userId={selectedAssignee ? selectedAssignee.value : 0} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={4}>
          <Card>
            <Card.Header>
              <Card.Title style={{marginBottom: "-20px"}}>Task Average Days to First Touch</Card.Title>
            </Card.Header>
            <Card.Body className="d-flex">
             <EmployeeKpisDaysToFirstTouch userId={selectedAssignee ? selectedAssignee.value : 0} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
}


const RevenueTeamList = () => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    sessionStorage.removeItem("revenueTeamId");
    reload();
  }, [])

  const reload = () => {
    authenticatedFetch({
      path: "revenueteam",
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }

  var revenueTeamList = [];

  if (isLoaded)
  {
    revenueTeamList = data.map((revenueteam, i) => {
      var updatedStr = getActivityDateTimeDisplay(revenueteam.updated) + (revenueteam.updatedBy != null ? " by " + revenueteam.updatedBy : "");
      return (
        { key: revenueteam.id, id: revenueteam.id, name: revenueteam.name, state: revenueteam.state, 
          defaultTaskAssignee: revenueteam.defaultTaskAssignee, h3LeadershipOwner: revenueteam.h3LeadershipOwner, tier: revenueteam.tier, lastVisitDate: revenueteam.lastVisitDate, dueForVisit: revenueteam.dueForVisit,
          contractType: revenueteam.contractType, contractProviders: revenueteam.contractProviders, contractCarriers: revenueteam.contractCarriers, contractFacilities: revenueteam.contractFacilities, contractUsers: revenueteam.contractUsers,
          uniqueNpiCount: revenueteam.uniqueNpiCount, uniqueCarrierCount: revenueteam.uniqueCarrierCount, uniqueFacilityCount: revenueteam.uniqueFacilityCount, uniqueUserCount: revenueteam.uniqueUserCount, startDate:revenueteam.startDate,  updated: updatedStr,
          subRows: [{key: revenueteam.id, id: revenueteam.id, name: revenueteam.notes, state: updatedStr, defaultTaskAssignee: revenueteam.enrollmentNotificationEmails,  contractType: revenueteam.supportEmailAlias, contractProviders: revenueteam.taskResolutionReportEmails}]}
      )
    }, this);
  }
  
  return (
    <SortableRevenueTeamTable revenueTeamList={revenueTeamList} isLoaded={isLoaded} error={error} reload={reload} />
  );

};

const RevenueTeams = () => {
  // Get the task data here so we can get the count
  const [myTasksError, setMyTasksError] = useState(null);
  const [myTasksLoaded, setMyTasksLoaded] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const [teamTasksError, setTeamTasksError] = useState(null);
  const [teamTasksLoaded, setTeamTasksLoaded] = useState(false);
  const [teamTasks, setTeamTasks] = useState([]);
  const [title, setTitle] = useState("Admin Portal | Revenue Teams");
  const [forceWorkloadRefresh, setForceWorkloadRefresh] = useState(0);

  const [selectedTeamTaskView, setSelectedTeamTaskView] = useState("DUE_NOW");
  // Due Now / All toggles default to what needs attention today (due today or earlier)
  const [showAllMyTasks, setShowAllMyTasks] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);

  const [selectedProviderId, setSelectedProviderId] = useState(null);

  const { authorities } = useContext(UserSettingsContext);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notyf = useContext(NotyfContext);

  const selectRevenueTeam = (revenueTeamId) => {
    authenticatedFetch({
      path: "auth/revenueteam",
      method: "PUT",
      requestData: {revenueTeamId: revenueTeamId},
      successHandler: function() {
        sessionStorage.setItem("revenueTeamId", revenueTeamId);
          navigate("/?destination=" + encodeURIComponent(searchParams.get("destination")));
      },
      errorHandler: function() {
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  useEffect(() => {
    reloadTaskLists();

    if (searchParams.get("destination") && searchParams.get("destination") !== '')
    {
      const decoded = decodeURIComponent(searchParams.get("destination")) // tasks?task=123, or practice/456, or provider/789
      const url = new URL(decoded, window.location.origin); // parse relative as full URL
      if (url.pathname === "/tasks") 
      {
        const taskId = parseInt(url.searchParams.get("task"), 10);
        if (!isNaN(taskId)) 
        {
          authenticatedFetch({
            path: "auth/revenueteam/task/" + taskId,
            successHandler: function(result) {
              selectRevenueTeam(result.id);
            },
            errorHandler: function(error) {
              if (error.message && parseInt(error.message) === 418)
              {
                notyf.open({type: "warning", message: "It looks like you don't have access to that revenue team.", duration: 5000, dismissable: true, ripple: true});
              }
              else
              {
                notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
              }
            }
          });
        }
      }
      else if (url.pathname === "/fppetasks") 
      {
        const taskId = parseInt(url.searchParams.get("task"), 10);
        if (!isNaN(taskId)) 
        {
          authenticatedFetch({
            path: "auth/revenueteam/fppetask/" + taskId,
            successHandler: function(result) {
              selectRevenueTeam(result.id);
            },
            errorHandler: function(error) {
              if (error.message && parseInt(error.message) === 418)
              {
                notyf.open({type: "warning", message: "It looks like you don't have access to that revenue team.", duration: 5000, dismissable: true, ripple: true});
              }
              else
              {
                notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
              }
            }
          });
        }
      } 
      else if (/^\/practice\/\d+$/.test(url.pathname)) 
      {
        const practiceId = parseInt(url.pathname.split("/")[2], 10);
        if (!isNaN(practiceId))
        {
          authenticatedFetch({
            path: "auth/revenueteam/practice/" + practiceId,
            successHandler: function(result) {
              selectRevenueTeam(result.id);
            },
            errorHandler: function(error) {
              if (error.message && parseInt(error.message) === 418)
              {
                notyf.open({type: "warning", message: "It looks like you don't have access to that revenue team.", duration: 5000, dismissable: true, ripple: true});
              }
              else
              {
                notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
              }
            }
          });
        }
      } 
      else if (/^\/provider\/\d+$/.test(url.pathname)) 
      {
        const providerId = parseInt(url.pathname.split("/")[2], 10);
        if (!isNaN(providerId))
        {
          authenticatedFetch({
            path: "auth/revenueteam/provider/" + providerId,
            successHandler: function(result) {
              selectRevenueTeam(result.id);
            },
            errorHandler: function(error) {
              if (error.message && parseInt(error.message) === 418)
              {
                notyf.open({type: "warning", message: "It looks like you don't have access to that revenue team.", duration: 5000, dismissable: true, ripple: true});
              }
              else
              {
                notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
              }
            }
          });
        }
      }
    }
  }, []) // Ideally would listen to team task type and selected assignee changes here, but don't want to reload my tasks, or team tasks if we have a null assignee etc

  const handleTeamTaskViewChanged = (newTeamTaskView) => {
    setSelectedTeamTaskView(newTeamTaskView);
    setSelectedAssignee(null); // Reset assignee when changing view

    if (newTeamTaskView === "DUE_NOW")
    {
      setTeamTasksLoaded(false);
      authenticatedFetch({
        path: "task/teamtasks",
        successHandler: function(result) {
          setTeamTasksLoaded(true);
          setTeamTasks(result);
        },
        errorHandler: function(error) {
          setTeamTasksLoaded(true);
          setTeamTasksError(error);
        }
      });
    }
    else
    {
      setTeamTasks([]); // Clear team tasks when switching to ASSIGNEE view
    }
  }

  const handleAssigneesChanged = (assignee) => {
    setSelectedAssignee(assignee);
    if (assignee == null || assignee.value === "")
    {
      setTeamTasks([]);
    }
    else
    {
      setTeamTasksLoaded(false);
      authenticatedFetch({
        path: "task/teamtasks/" + assignee.value,
        successHandler: function(result) {
          setTeamTasksLoaded(true);
          setTeamTasks(result);
        },
        errorHandler: function(error) {
          setTeamTasksLoaded(true);
          setTeamTasksError(error);
        }
      });
    }
  }

  const handleSelectedProviderChanged = (provider) => {
    authenticatedFetch({
      path: "auth/revenueteam",
      method: "PUT",
      requestData: {revenueTeamId: provider.revenueTeamId},
      successHandler: function() {
          setSelectedProviderId(null);
          sessionStorage.setItem("revenueTeamId", provider.revenueTeamId);
          navigate("/provider/" + provider.value);
          window.scrollTo(0, 0);
      },
      errorHandler: function() {
        setSelectedProviderId(null);  // Clear the field
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const reloadTaskLists = () => {
    // setMyTasksLoaded(false);
    authenticatedFetch({
      path: "task/mytasks",
      successHandler: function(result) {
        setMyTasksLoaded(true);
        setMyTasks(result);
      },
      errorHandler: function(error) {
        setMyTasksLoaded(true);
        setMyTasksError(error);
      }
    });

    // setTeamTasksLoaded(false);
    authenticatedFetch({
      path: "task/teamtasks" + (selectedTeamTaskView === "ASSIGNEE" ? (selectedAssignee ? "/" + selectedAssignee.value : "") : ""),
      successHandler: function(result) {
        setTeamTasksLoaded(true);
        setTeamTasks(result);
      },
      errorHandler: function(error) {
        setTeamTasksLoaded(true);
        setTeamTasksError(error);
      }
    });

    setForceWorkloadRefresh(forceWorkloadRefresh + 1);
  }

  const canViewTeamWorkload = authorities.includes("H3_ADMIN_WORKLOAD_VIEW") || authorities.includes("H3_ADMIN_WORKLOAD_EDIT");
  const canViewEmployeeKpis = authorities.includes("H3_ADMIN_EMPLOYEE_KPIS_VIEW") || authorities.includes("H3_ADMIN_EMPLOYEE_KPIS_EDIT");


  var myTasksTitle = "My Tasks";
  if (myTasksLoaded && !myTasksError && myTasks != null)
  {
    const today = getTodayString();
    const myTasksCount = showAllMyTasks ? myTasks.length : myTasks.filter((task) => isDueNow(task, today)).length;
    myTasksTitle = "My Tasks (" + myTasksCount + ")";
  }

  var teamTasksTitle = "Team Tasks";
  if (teamTasksLoaded && !teamTasksError && teamTasks != null)
  {
    teamTasksTitle = "Team Tasks (" + teamTasks.length + ")";
  }

  const handleTabSelect = (key) => {
    if (key === "second")
    {
      setTitle("Admin Portal | " + myTasksTitle);
    }
    else if (key === "third")
    {
      setTitle("Admin Portal | " + teamTasksTitle);
    }
    else if (key === "fourth")
    {
      setTitle("Admin Portal | Team Workload");
    }
    else if (key === "fifth")
    {
      setTitle("Admin Portal | Employee KPIs");
    }
    else
    {
      setTitle("Admin Portal | Revenue Teams");
    }
  }

  return (
    <>
    <React.Fragment>
        <Helmet title={title}/>
        <Tab.Container fluid className="p-0" defaultActiveKey="first" onSelect={(key) => {handleTabSelect(key)}}>
          
          <div className="border-bottom">
            <Row className="align-items-end">
              {/* Tabs */}
              <Col sm={12} md={8}>
                <Nav variant="tabs" className="border-bottom-0">
                  <Nav.Item>
                    <Nav.Link eventKey="first">Revenue Teams</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="second">{myTasksTitle}</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="third">{teamTasksTitle}</Nav.Link>
                  </Nav.Item>
                  {canViewTeamWorkload && (
                    <Nav.Item>
                      <Nav.Link eventKey="fourth">Team Workload</Nav.Link>
                    </Nav.Item>
                  )}
                  {canViewEmployeeKpis && (
                    <Nav.Item>
                      <Nav.Link eventKey="fifth">Employee KPIs</Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
              </Col>
              <Col sm={12} md={4} className="pt-2 pt-md-0">
                <ProviderSelect
                  selectedProviderId={selectedProviderId}
                  handleProviderChanged={handleSelectedProviderChanged}
                />
              </Col>
            </Row>
          </div>
          <Row>
            <Col lg="12">
              <Tab.Content>
                <Tab.Pane eventKey="first">
                  <RevenueTeamList />
                </Tab.Pane>
                <Tab.Pane eventKey="second">
                  <TaskList taskTab="mytasks" showAllDue={showAllMyTasks} setShowAllDue={setShowAllMyTasks} error={myTasksError} isLoaded={myTasksLoaded} data={myTasks} reloadTaskLists={reloadTaskLists}/>
                </Tab.Pane>
                <Tab.Pane eventKey="third">
                  <TaskList taskTab="teamtasks" error={teamTasksError} isLoaded={teamTasksLoaded} data={teamTasks} reloadTaskLists={reloadTaskLists} 
                    selectedTeamTaskView={selectedTeamTaskView} handleTeamTaskViewChanged={handleTeamTaskViewChanged} 
                    handleAssigneesChanged={handleAssigneesChanged} selectedAssignee={selectedAssignee} teamTasksLoaded={teamTasksLoaded}/>
                </Tab.Pane>
                {canViewTeamWorkload &&
                <Tab.Pane eventKey="fourth">
                  <TeamWorkload refresh={forceWorkloadRefresh}/>
                </Tab.Pane>
                }
                {canViewEmployeeKpis &&
                <Tab.Pane eventKey="fifth">
                  <EmployeeKpis/>
                </Tab.Pane>
                }
              </Tab.Content>
            </Col>
        </Row>
        </Tab.Container>
        <div style={{ width: "155px", height: "70px", overflow: "hidden", marginRight: "2px" }} className="float-end">
          <img alt="Please sign-in to H3 Google account" height="70px" src="https://docs.google.com/spreadsheets/d/e/2PACX-1vTRsNko74t5yyhcxUurlWquLfymLUiOo3cRur3W7wTkvecFmv6fTHLc4XfXlAW6hZOEl2ss-hJxKzDp/pubchart?oid=1500971844&format=image" />
        </div>
    </React.Fragment>
    </>
  );
};

export default RevenueTeams;
